import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/app/http";
import { screenBudgetQueryKeys } from "./queryKeys";

interface UseScreenBudgetEventsOptions {
  familyId: number | null;
  enabled: boolean;
  onUnauthorized: () => void;
}

export function useScreenBudgetEvents({ familyId, enabled, onUnauthorized }: UseScreenBudgetEventsOptions) {
  const queryClient = useQueryClient();
  const [isStreamHealthy, setIsStreamHealthy] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const authCheckThrottleRef = useRef(0);

  useEffect(() => {
    if (!enabled || familyId === null) {
      setIsStreamHealthy(false);
      return;
    }

    const eventSource = new EventSource(`/api/services/screen-budget/v1/families/${familyId}/events`);

    const scheduleRefresh = () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: screenBudgetQueryKeys.kids(familyId) });
      }, 250);
    };

    const handleDashboardRefresh = () => {
      scheduleRefresh();
    };

    eventSource.addEventListener("dashboard-refresh", handleDashboardRefresh);

    eventSource.onopen = () => {
      setIsStreamHealthy(true);
    };

    eventSource.onerror = () => {
      setIsStreamHealthy(false);

      const now = Date.now();
      if (now - authCheckThrottleRef.current > 10_000) {
        authCheckThrottleRef.current = now;
        void requestJson<{ user?: unknown }>("/api/auth/me").then(result => {
          if (!result.ok && result.status === 401) {
            onUnauthorized();
          }
        });
      }
    };

    return () => {
      eventSource.removeEventListener("dashboard-refresh", handleDashboardRefresh);
      eventSource.close();
      setIsStreamHealthy(false);
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [enabled, familyId, onUnauthorized, queryClient]);

  return {
    isStreamHealthy,
  };
}
